import { ColumnDef } from "@tanstack/react-table";
import parsePhoneNumberFromString from "libphonenumber-js";
import { useNavigate } from "react-router-dom";
import { personnelLoader } from "./loaders/personnel-loader";
import { useAsync, useUrlParams } from "@/shared/hooks";
import { LeagueId, SunsApiError } from "@suns/api";
import { teamsLoader } from "../teams/loader/team-loader";
import { useMemo } from "react";
import { DataTable, Filter, Flex, Text } from "@suns/design-system";
import { TeamLogo } from "@/components";

interface Personnel {
  id: number;
  lastName: string;
  firstName: string;
  mobilePhone: string | null;
  workPhone: string | null;
  email: string | null;
  currentTeam?: {
    id: number;
    nbaId: number | null;
    domesticLeagueId: number;
    name: string;
    fullName: string | null;
    image: string | null;
  } | null;
  positions?: {
    position: string;
  }[];
}

const columns: ColumnDef<Personnel>[] = [
  {
    header: "Name",
    accessorFn: (row: Personnel) =>
      row ? row.lastName + ", " + row.firstName : "--",
    enableHiding: false,
    enableSorting: false,
  },
  {
    header: "Team",
    accessorKey: "currentTeam",
    enableHiding: false,
    enableSorting: false,
    cell: (cell) => {
      const team = cell.row.original.currentTeam;
      if (!team) return "--";
      return (
        <Flex align="center" justify="center" gap="xs">
          <TeamLogo
            src={team.image}
            nbaTeamId={team.nbaId || team.id}
            leagueId={team.domesticLeagueId}
            size="3xs"
          />
          <Text size="sm" key={team.id}>
            {team.fullName ?? team.name}
          </Text>
        </Flex>
      );
    },
  },
  {
    header: "Position(s)",
    accessorKey: "positions",
    enableHiding: false,
    enableSorting: false,
    accessorFn: (row: Personnel) =>
      row.positions && row.positions.length > 0
        ? row.positions.map((p) => p.position).join(", ")
        : "--",
  },
  {
    header: "Mobile Phone#",
    accessorKey: "mobilePhone",
    accessorFn: (row: Personnel) =>
      parsePhoneNumberFromString(row.mobilePhone ?? "")?.format(
        "INTERNATIONAL"
      ) ?? "--",
    enableHiding: false,
    enableSorting: false,
  },
  {
    header: "Work Phone#",
    accessorKey: "workPhone",
    accessorFn: (row: Personnel) =>
      row?.workPhone
        ? parsePhoneNumberFromString(row.workPhone)?.format("INTERNATIONAL")
        : "--",
    enableHiding: false,
    enableSorting: false,
  },
  {
    header: "Email",
    accessorKey: "email",
    accessorFn: (row: Personnel) => row?.email ?? "--",
    enableHiding: false,
    enableSorting: false,
  },
];

const defaultParams = {
  page: "1",
  pageSize: "30",
  sort: "lastName",
  dir: "asc",
};

export function PersonnelListing() {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useUrlParams(
    ["page", "pageSize", "sort", "dir", "teamIds"],
    defaultParams
  );

  const sorting = {
    column: searchParams.sort || "lastName",
    dir: searchParams.dir == "asc",
  };
  const pagination = {
    pageIndex: parseInt(searchParams.page || "1") - 1,
    pageSize: parseInt(searchParams.pageSize || "20"),
  };

  const teamIdParams = searchParams.teamIds;
  const teamIds = teamIdParams?.split(",");

  const {
    response: personnel,
    loading: personnelLoading,
    error: personnelError,
  } = useAsync(personnelLoader, {
    offset: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
    sortColumn: sorting.column,
    sortDir: sorting.dir ? "asc" : "desc",
    currentTeamIds: teamIds?.map(Number),
  });

  const {
    response: teams,
    loading: teamsLoading,
    error: teamsError,
  } = useAsync(teamsLoader, {
    domesticLeagueId: [LeagueId.NBA],
  });

  const handleSelectPersonnel = (id: number | null) => {
    if (!id) return;
    navigate(`${id}`);
  };

  const updateSearchParams = (params = {}) => {
    setSearchParams({
      ...searchParams,
      ...params,
    });
  };

  const handleFilterChange = (selectedKeys: string[]) => {
    if (selectedKeys.length === 0) {
      const { teamIds, ...rest } = searchParams;
      setSearchParams(rest);
    } else {
      updateSearchParams({
        teamIds: selectedKeys.join(","),
      });
    }
  };

  const filterTeamsOptions = useMemo(
    () =>
      teams?.teams
        .map((team) => ({
          key: team.id.toString(),
          name: team.fullName ?? "",
        }))
        .sort((teamA, teamB) => {
          const nameA = teamA.name ?? "";
          const nameB = teamB.name ?? "";
          return nameA.localeCompare(nameB);
        }) || [],
    [teams?.teams]
  );

  if (personnelError) {
    throw new SunsApiError("Error loading personnel", {
      cause: personnelError,
    });
  }

  if (teamsError) {
    throw new SunsApiError("Error loading teams", {
      cause: teamsError,
    });
  }

  return (
    <Flex direction="down" gap="md">
      <Flex>
        <Filter
          title={teamsLoading ? "Loading..." : "Teams"}
          value={teamIds ?? []}
          options={filterTeamsOptions}
          onChange={handleFilterChange}
        />
      </Flex>
      <DataTable
        loading={personnelLoading}
        columns={columns}
        data={personnel?.personnel || []}
        count={personnel?.count || 0}
        pagination={pagination}
        setPagination={(setPagination) => {
          const newPagination =
            typeof setPagination === "function"
              ? setPagination(pagination)
              : setPagination;

          updateSearchParams({
            page: `${newPagination.pageIndex + 1}`,
            pageSize: `${newPagination.pageSize}`,
          });
        }}
        onRowClicked={(row) => handleSelectPersonnel(row.original.id)}
      />
    </Flex>
  );
}
